import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {Sorting} from 'features/core/shared/misc/sorting';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {CnAgentPromptDto} from '../../../model/dto/api/cn-agent-prompt.dto';
import {CnImportDraftDto} from '../../../model/dto/api/cn-import-draft.dto';
import {CnImportApproveResultDto} from '../../../model/dto/api/cn-import-approve-result.dto';
import {CnImportStatementDetailDto} from '../../../model/dto/api/cn-import-statement-detail.dto';

/**
 * API service for the cn commission-import endpoints.
 *
 * Endpoint mapping:
 *   GET /cn/import-profile/agent-prompt  → getAgentPrompt()
 *   GET /cn/import/{statementId}         → getStatementDetail()
 *
 * Partner options for dropdowns:
 *   loadPartnerOptions()  — QL over cn.CommissionPartner → cm.Partner (id, name)
 *
 * The QL used by loadPartnerOptions() is the canonical partner-dropdown query shared with
 * CnReconciliationPanelComponent. Factored here so the import component can reuse it without
 * duplicating the query.
 */
@Injectable({
    providedIn: 'root'
})
export class CnImportApiService {

    private readonly importProfileBaseUrl = MvsCrudService.baseUrl + '/cn/import-profile';
    private readonly importBaseUrl = MvsCrudService.baseUrl + '/cn/import';

    constructor(
        private http: HttpClient,
        private qlService: QlService
    ) {
    }

    /**
     * GET /cn/import-profile/agent-prompt?commissionPartnerId={id}
     * Returns the import-profile metadata and the rendered AI agent prompt for the given
     * CommissionPartner. Callers should handle the case where profileConfigured is false
     * (the partner exists but has no import profile yet).
     *
     * @param commissionPartnerId  the ID of the CommissionPartner to load the profile for
     */
    getAgentPrompt(commissionPartnerId: number): Observable<CnAgentPromptDto> {
        const params = new HttpParams().set('commissionPartnerId', String(commissionPartnerId));
        return this.http.get<CnAgentPromptDto>(`${this.importProfileBaseUrl}/agent-prompt`, {params});
    }

    /**
     * GET /cn/import/drafts[?commissionPartnerId={id}]
     * Returns all CommissionStatement records in DRAFT status awaiting the approve/reject decision.
     *
     * The commissionPartnerId is OPTIONAL: when omitted the backend returns ALL open drafts across
     * every Gesellschaft (the global Eingangsfreigabe list); when supplied the result is scoped to
     * that single CommissionPartner. The param is kept for backward compatibility with any caller
     * that still needs the partner-scoped variant.
     *
     * @param commissionPartnerId  optional ID of the CommissionPartner to scope the drafts to
     */
    listImportDrafts(commissionPartnerId?: number): Observable<CnImportDraftDto[]> {
        let params = new HttpParams();
        if (commissionPartnerId != null) {
            params = params.set('commissionPartnerId', String(commissionPartnerId));
        }
        return this.http.get<CnImportDraftDto[]>(`${this.importBaseUrl}/drafts`, {params});
    }

    /**
     * POST /cn/import/approve-bulk
     * Approves a set of CommissionStatements in one request. The backend creates commission
     * activities and triggers reconciliation runs for each approved statement.
     *
     * @param ids  array of statement IDs to approve
     */
    approveDrafts(ids: number[]): Observable<CnImportApproveResultDto[]> {
        return this.http.post<CnImportApproveResultDto[]>(`${this.importBaseUrl}/approve-bulk`, {ids});
    }

    /**
     * POST /cn/import/reject-bulk
     * Rejects a set of CommissionStatements in one request. Returns the IDs of the
     * statements that were successfully rejected.
     *
     * @param ids     array of statement IDs to reject
     * @param reason  human-readable rejection reason stored on each statement
     */
    rejectDrafts(ids: number[], reason: string): Observable<number[]> {
        return this.http.post<number[]>(`${this.importBaseUrl}/reject-bulk`, {ids, reason});
    }

    /**
     * GET /cn/import/{statementId}
     * Returns the full detail view for one CommissionStatement, including all
     * booking entries (entries) and individual commission lines (lines).
     *
     * The linked DM document id is NOT part of this response. To obtain it,
     * load the CommissionStatement entity via the standard CRUD endpoint
     * GET /mvsa/cn/commissionStatements/{id} and read `dmDocument`.
     *
     * @param statementId  ID of the CommissionStatement to load
     */
    getStatementDetail(statementId: number): Observable<CnImportStatementDetailDto> {
        return this.http.get<CnImportStatementDetailDto>(`${this.importBaseUrl}/${statementId}`);
    }

    /**
     * Load the selectable partner list for Partner-Auswahl dropdowns via QL.
     *
     * Restricted to the CONFIGURED commission partners only: we do not receive commissions from
     * every partner, so the list is a much smaller, configured set. The query starts at
     * cn.CommissionPartner (alias e) and joins N:1 to its `partner` relation (cm.Partner, alias p).
     *
     * The option VALUE is e.id (cn.CommissionPartner.id) because ALL cn import endpoints are keyed
     * by commissionPartnerId = cn.CommissionPartner.id. The option LABEL is p.name (cm.Partner name).
     * Dedup is performed on e.id (defensive; there is one CommissionPartner per partner).
     *
     * Returns an array of {id, name} objects sorted by name ascending.
     */
    loadPartnerOptions(): Observable<{ id: number; name: string }[]> {
        return this.qlService.builder()
            .query({
                name: 'cnImportPartnerOptions',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cn.CommissionPartner',
                    as: 'e',
                    joins: [
                        {
                            joinType: 'left',
                            type: 'entity',
                            name: 'partner',
                            as: 'p'
                        }
                    ]
                },
                sortings: [
                    new Sorting('p.name', true)
                ],
                paging: PagingDto.createSize(500)
            })
            .execute()
            .pipe(
                map((result) => {
                    const rows: any[] = result.get('cnImportPartnerOptions') ?? [];
                    const seen = new Set<number>();
                    const options: { id: number; name: string }[] = [];
                    for (const r of rows) {
                        const id: number = r['e.id'];
                        if (id == null || seen.has(id)) {
                            continue;
                        }
                        seen.add(id);
                        options.push({id, name: r['p.name'] ?? `Partner #${id}`});
                    }
                    return options;
                })
            );
    }

}
